import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../../components/Layout/Layout';
import LeadsTabs from './LeadsTabs';
import {
  Card, Button, Badge, Modal, Tooltip, EmptyState, Spinner, Select,
  Table, TableHead, TableBody, TableRow, TableCell, StatCard,
} from '../../components/UI';
import { reEngagementAPI, usersAPI, leadsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineRefresh, HiOutlinePlus, HiOutlinePencil, HiOutlineTrash,
  HiOutlinePlay, HiOutlineUserAdd, HiOutlineUserRemove, HiOutlineClock,
} from 'react-icons/hi';
import ReEngagementCampaignModal from './ReEngagementCampaignModal';

const BUCKETS = [
  { value: 'lost', label: 'Lost', description: 'Leads marked as lost' },
  { value: 'cold', label: 'Cold', description: 'Leads currently marked cold' },
  { value: 'inactive30', label: 'Inactive 30+ days' },
  { value: 'inactive60', label: 'Inactive 60+ days' },
  { value: 'inactive90', label: 'Inactive 90+ days' },
  { value: 'neverContacted', label: 'Never contacted' },
  { value: 'overdue', label: 'Overdue follow-ups' },
];

const CAMPAIGN_STATUS_BADGES = {
  draft: 'default',
  active: 'success',
  paused: 'warning',
  completed: 'info',
  cancelled: 'danger',
};

const STATUS_BADGES = {
  new: 'new', open: 'info', in_progress: 'primary', follow_up: 'follow_up',
  cold: 'cold', warm: 'warm', hot: 'hot', converted: 'converted',
  closed: 'closed', lost: 'danger', not_connected: 'not_connected',
};

const describeCampaignFilters = (campaign = {}) => {
  const parts = [];
  const filters = campaign.filters || {};
  if (filters.status) parts.push(`Status: ${filters.status.replace(/_/g, ' ')}`);
  if (filters.stage) parts.push(`Stage: ${filters.stage.replace(/_/g, ' ')}`);
  if (filters.city) parts.push(`City: ${filters.city}`);
  if (filters.source) parts.push(`Source: ${filters.source}`);
  if (filters.priority) parts.push(`Priority: ${filters.priority}`);
  if (filters.minLeadScore !== undefined || filters.maxLeadScore !== undefined) {
    parts.push(`Score ${filters.minLeadScore ?? 0}–${filters.maxLeadScore ?? 100}`);
  }
  const minDays = campaign.trigger_conditions?.days_since_last_contact_min;
  const maxDays = campaign.trigger_conditions?.days_since_last_contact_max;
  if (minDays !== undefined || maxDays !== undefined) {
    parts.push(`Inactive ${minDays ?? 0}–${maxDays ?? '∞'} days`);
  }
  return parts;
};

const ReEngagement = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('leads');
  const [selectedBucket, setSelectedBucket] = useState('lost');
  const [bucketPage, setBucketPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [assignTo, setAssignTo] = useState('');
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [showRunModal, setShowRunModal] = useState(false);
  const [runCampaign, setRunCampaign] = useState(null);
  const pageSize = 20;

  // Summary bucket counts
  const { data: summary } = useQuery({
    queryKey: ['reEngagementSummary'],
    queryFn: async () => {
      const response = await reEngagementAPI.getSummary();
      return response.data?.data || {};
    },
    staleTime: 30000,
  });

  // Leads in the selected bucket
  const { data: bucketData, isLoading: bucketLoading } = useQuery({
    queryKey: ['reEngagementBucketLeads', selectedBucket, bucketPage],
    queryFn: async () => {
      const response = await reEngagementAPI.getBucketLeads(selectedBucket, {
        page: bucketPage,
        limit: pageSize,
      });
      return response.data || {};
    },
  });

  // Telecallers for re-engagement outreach
  const { data: telecallers } = useQuery({
    queryKey: ['telecallers'],
    queryFn: async () => {
      const response = await usersAPI.getAll({ role: 'telecaller', limit: 200 });
      return response.data?.data || [];
    },
  });

  // Campaigns
  const { data: campaignsData, isLoading: campaignsLoading } = useQuery({
    queryKey: ['reEngagementCampaigns'],
    queryFn: async () => {
      const response = await reEngagementAPI.getCampaigns();
      return response.data?.data || [];
    },
    enabled: activeTab === 'campaigns',
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => reEngagementAPI.deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['reEngagementCampaigns']);
      toast.success('Campaign deleted successfully');
      setShowDeleteModal(false);
      setCampaignToDelete(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete campaign');
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => reEngagementAPI.setCampaignStatus(id, status),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries(['reEngagementCampaigns']);
      toast.success(response.data?.message || `Campaign ${variables.status}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update campaign status');
    },
  });

  const runMutation = useMutation({
    mutationFn: ({ id, assignTo }) => reEngagementAPI.runCampaign(id, assignTo || undefined),
    onSuccess: (response) => {
      const d = response.data?.data || {};
      toast.success(response.data?.message || `Created ${d.followUpsCreated} follow-up task(s)`);
      if (d.skippedNoAssignee > 0) {
        toast.error(`${d.skippedNoAssignee} lead(s) skipped — unassigned and no default telecaller chosen`);
      }
      queryClient.invalidateQueries(['reEngagementCampaigns']);
      queryClient.invalidateQueries(['reEngagementSummary']);
      setShowRunModal(false);
      setRunCampaign(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to run campaign');
    },
  });

  const assignMutation = useMutation({
    mutationFn: (leadIds) => leadsAPI.bulkAssign({ leadIds, assignedTo: assignTo }),
    onSuccess: () => {
      toast.success('Leads assigned for re-engagement');
      setSelectedIds([]);
      queryClient.invalidateQueries(['reEngagementBucketLeads']);
      queryClient.invalidateQueries(['reEngagementSummary']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to assign leads');
    },
  });

  const bucketLeads = bucketData?.data || [];
  const bucketTotal = bucketData?.pagination?.total || 0;
  const bucketTotalPages = bucketData?.pagination?.totalPages || 1;
  const campaigns = campaignsData || [];

  const allPageSelected = bucketLeads.length > 0 && bucketLeads.every((l) => selectedIds.includes(l._id));
  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !bucketLeads.some((l) => l._id === id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...bucketLeads.map((l) => l._id)])]);
    }
  };
  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleAssign = () => {
    if (!assignTo) {
      toast.error('Select a telecaller to assign to');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Select at least one lead');
      return;
    }
    assignMutation.mutate(selectedIds);
  };

  const summaryCards = [
    { title: 'Lost leads', value: summary?.lost ?? 0, color: 'danger' },
    { title: 'Cold leads', value: summary?.cold ?? 0, color: 'info' },
    { title: 'Inactive 30+ days', value: summary?.inactive30 ?? 0, color: 'warning' },
    { title: 'Inactive 60+ days', value: summary?.inactive60 ?? 0, color: 'warning' },
    { title: 'Inactive 90+ days', value: summary?.inactive90 ?? 0, color: 'danger' },
    { title: 'Never contacted', value: summary?.neverContacted ?? 0, color: 'secondary' },
    { title: 'Overdue follow-ups', value: summary?.overdue ?? 0, color: 'danger' },
  ];

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Leads section tabs */}
        <LeadsTabs />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Re-engagement</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Identify inactive or lost leads and win them back with targeted outreach
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={<HiOutlinePlus className="w-4 h-4" />}
            onClick={() => { setSelectedCampaign(null); setShowCampaignModal(true); }}
          >
            <span className="hidden sm:inline">New Campaign</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          {summaryCards.map((c) => (
            <StatCard key={c.title} title={c.title} value={c.value} color={c.color} />
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          <button
            type="button"
            onClick={() => setActiveTab('leads')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === 'leads'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Inactive & Lost Leads
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('campaigns')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === 'campaigns'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Re-engagement Campaigns
          </button>
        </div>

        {/* Leads tab */}
        {activeTab === 'leads' && (
          <div className="space-y-4">
            {/* Bucket chips */}
            <div className="flex flex-wrap gap-2">
              {BUCKETS.map((b) => (
                <button
                  key={b.value}
                  type="button"
                  title={b.description || ''}
                  onClick={() => { setSelectedBucket(b.value); setBucketPage(1); setSelectedIds([]); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    selectedBucket === b.value
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>

            {/* Outreach action bar */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-secondary-50 rounded-xl border border-secondary-200">
              <span className="text-sm font-semibold text-secondary-700 flex-shrink-0">
                Win-back outreach:
              </span>
              {(telecallers || []).length === 0 ? (
                <p className="text-sm text-warning-700 bg-warning-50 border border-warning-200 rounded-lg px-3 py-2 flex-1">
                  No telecallers found in your organization. Add users with the{' '}
                  <strong>Telecaller</strong> role in CRM → Users first.
                </p>
              ) : (
                <select
                  value={assignTo}
                  onChange={(e) => setAssignTo(e.target.value)}
                  className="px-3 py-2 border border-secondary-200 rounded-lg text-sm bg-white flex-1 min-w-0"
                >
                  <option value="">Select telecaller…</option>
                  {(telecallers || []).map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.firstName} {u.lastName}
                    </option>
                  ))}
                </select>
              )}
              <Button
                variant="primary"
                size="sm"
                icon={<HiOutlineUserAdd className="w-4 h-4" />}
                onClick={handleAssign}
                loading={assignMutation.isPending}
                disabled={selectedIds.length === 0 || (telecallers || []).length === 0}
                className="flex-shrink-0"
              >
                Assign {selectedIds.length > 0 ? `${selectedIds.length} ` : ''}selected
              </Button>
            </div>

            {/* Leads table */}
            {bucketLoading ? (
              <div className="flex justify-center py-10">
                <Spinner size="lg" />
              </div>
            ) : bucketLeads.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<HiOutlineUserRemove className="w-8 h-8 text-gray-400" />}
                  title="No leads in this bucket"
                  description="Great — nothing needs re-engaging here right now"
                />
              </Card>
            ) : (
              <Card padding="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHead>
                      <TableRow hoverable={false}>
                        <TableCell header className="w-10">
                          <input
                            type="checkbox"
                            checked={allPageSelected}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                          />
                        </TableCell>
                        <TableCell header>Name</TableCell>
                        <TableCell header>Phone</TableCell>
                        <TableCell header>Status</TableCell>
                        <TableCell header>Last contacted</TableCell>
                        <TableCell header>Created</TableCell>
                        <TableCell header>Assigned</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bucketLeads.map((lead) => (
                        <TableRow key={lead._id}>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(lead._id)}
                              onChange={() => toggleSelect(lead._id)}
                              className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                            />
                          </TableCell>
                          <TableCell>
                            <span className="font-medium text-gray-900">{lead.name}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-gray-600">{lead.phone}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant={STATUS_BADGES[lead.status] || 'default'} size="sm">
                              {lead.status?.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-gray-600 text-sm">
                              {lead.lastContactedAt
                                ? new Date(lead.lastContactedAt).toLocaleDateString()
                                : <span className="text-red-500 font-medium">Never</span>}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-gray-500 text-sm">
                              {new Date(lead.createdAt).toLocaleDateString()}
                            </span>
                          </TableCell>
                          <TableCell>
                            {lead.assignedTo ? (
                              <span className="text-gray-700 text-sm">
                                {lead.assignedTo.firstName} {lead.assignedTo.lastName}
                              </span>
                            ) : (
                              <Badge variant="warning" size="sm">Unassigned</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {bucketTotal > 0 && (
                  <div className="flex items-center justify-between p-3 border-t border-gray-100">
                    <span className="text-xs sm:text-sm text-gray-500">
                      Showing {(bucketPage - 1) * pageSize + 1}–{Math.min(bucketPage * pageSize, bucketTotal)} of {bucketTotal}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={bucketPage === 1}
                        onClick={() => setBucketPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <span className="text-xs text-gray-500">Page {bucketPage} of {bucketTotalPages}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={bucketPage >= bucketTotalPages}
                        onClick={() => setBucketPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>
        )}

        {/* Campaigns tab */}
        {activeTab === 'campaigns' && (
          <>
            {campaignsLoading ? (
              <div className="flex justify-center py-12">
                <Spinner size="lg" />
              </div>
            ) : campaigns.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<HiOutlineRefresh className="w-8 h-8 text-gray-400" />}
                  title="No re-engagement campaigns yet"
                  description="Create a campaign to automatically target inactive or lost leads and generate win-back follow-up tasks"
                />
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                {campaigns.map((campaign) => (
                  <Card key={campaign._id} className="flex flex-col justify-between hover:shadow-lg transition-all duration-200">
                    <div>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <HiOutlineRefresh className="w-5 h-5 text-primary-600" />
                          </span>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{campaign.name}</p>
                            <p className="text-xs text-gray-400">
                              {campaign.total_targeted_live} currently match
                            </p>
                          </div>
                        </div>
                        <Badge variant={CAMPAIGN_STATUS_BADGES[campaign.status] || 'default'} size="sm">
                          {campaign.status}
                        </Badge>
                      </div>

                      {campaign.description && (
                        <p className="text-xs text-gray-500 line-clamp-2 mb-3">{campaign.description}</p>
                      )}

                      <div className="flex flex-wrap gap-1 mb-3">
                        {describeCampaignFilters(campaign).slice(0, 4).map((part, i) => (
                          <Badge key={i} variant="default" size="sm">{part}</Badge>
                        ))}
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-center text-xs mb-3">
                        <div className="p-2 bg-secondary-50 rounded-lg">
                          <p className="font-bold text-secondary-900">{campaign.total_targeted || 0}</p>
                          <p className="text-secondary-400">Targeted</p>
                        </div>
                        <div className="p-2 bg-success-50 rounded-lg">
                          <p className="font-bold text-success-700">{campaign.total_re_engaged_live || 0}</p>
                          <p className="text-success-600">Re-engaged</p>
                        </div>
                        <div className="p-2 bg-secondary-50 rounded-lg">
                          <p className="font-bold text-secondary-900">{(campaign.steps || []).length}</p>
                          <p className="text-secondary-400">Steps</p>
                        </div>
                      </div>

                      {campaign.last_run_at && (
                        <p className="text-xs text-gray-400 flex items-center gap-1 mb-2">
                          <HiOutlineClock className="w-3.5 h-3.5" />
                          Last run: {new Date(campaign.last_run_at).toLocaleString()}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-end gap-1 pt-2 border-t border-gray-100">
                      <Tooltip text={campaign.status === 'active' ? 'Pause campaign' : 'Activate campaign'}>
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<HiOutlinePlay className={`w-4 h-4 ${campaign.status === 'active' ? 'text-warning-500 rotate-180' : 'text-success-600'}`} />}
                          onClick={() =>
                            statusMutation.mutate({
                              id: campaign._id,
                              status: campaign.status === 'active' ? 'paused' : 'active',
                            })
                          }
                          title={campaign.status === 'active' ? 'Pause' : 'Activate'}
                        />
                      </Tooltip>
                      <Tooltip text="Run now — create follow-up tasks for matching leads">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<HiOutlineRefresh className="w-4 h-4 text-primary-600" />}
                          onClick={() => { setRunCampaign(campaign); setShowRunModal(true); }}
                          title="Run now"
                        />
                      </Tooltip>
                      <Tooltip text="Edit campaign">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<HiOutlinePencil className="w-4 h-4 text-gray-500" />}
                          onClick={() => { setSelectedCampaign(campaign); setShowCampaignModal(true); }}
                          title="Edit"
                        />
                      </Tooltip>
                      <Tooltip text="Delete campaign">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                          onClick={() => { setCampaignToDelete(campaign); setShowDeleteModal(true); }}
                          title="Delete"
                        />
                      </Tooltip>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Campaign create/edit modal */}
        <ReEngagementCampaignModal
          isOpen={showCampaignModal}
          onClose={() => { setShowCampaignModal(false); setSelectedCampaign(null); }}
          campaign={selectedCampaign}
        />

        {/* Run campaign modal */}
        <RunCampaignModal
          isOpen={showRunModal}
          onClose={() => { setShowRunModal(false); setRunCampaign(null); }}
          campaign={runCampaign}
          telecallers={telecallers || []}
          runMutation={runMutation}
        />

        {/* Delete confirmation modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete Campaign"
          size="sm"
        >
          <div className="space-y-3 sm:space-y-4">
            <p className="text-gray-600 text-sm">
              Are you sure you want to delete{' '}
              <span className="font-semibold">{campaignToDelete?.name}</span>? Leads and created follow-ups are not affected.
            </p>
            <div className="flex gap-2 sm:gap-3 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => campaignToDelete && deleteMutation.mutate(campaignToDelete._id)}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

// ========================================
// Run campaign — choose who receives unassigned targeted leads
// ========================================
const RunCampaignModal = ({ isOpen, onClose, campaign, telecallers, runMutation }) => {
  const [assignTo, setAssignTo] = useState('');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Run Re-engagement Campaign" size="sm">
      <div className="space-y-3 sm:space-y-4">
        <p className="text-gray-600 text-sm">
          Run <span className="font-semibold">{campaign?.name}</span> now? Matching leads will get a
          follow-up call task for their assigned telecaller.
        </p>
        <Select
          label="Assign unassigned leads to"
          placeholder="Only leads with an assignee"
          value={assignTo}
          onChange={(e) => setAssignTo(e.target.value)}
          options={telecallers.map((u) => ({ value: u._id, label: `${u.firstName} ${u.lastName}` }))}
        />
        <div className="flex gap-2 sm:gap-3 justify-end">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<HiOutlinePlay className="w-4 h-4" />}
            onClick={() => runMutation.mutate({ id: campaign._id, assignTo })}
            loading={runMutation.isPending}
          >
            Run campaign
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReEngagement;